export interface BPReading {
  id?: number;
  patient_id: string;
  systolic: number;
  diastolic: number;
  pulse: number;
  mood?: number;
  date: string;
  comment?: string;
  created_at?: string;
}

const API_BASE_URL = 'https://ivanchuyko.com/sqlitenodejs/api';

export async function checkPatientExists(patientId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/patients`);
    const patients = await response.json();
    return patients.some((patient: { patient_id: string }) => patient.patient_id === patientId);
  } catch (error) {
    console.error('Error checking patient:', error);
    throw error;
  }
}

export async function createPatient(patientId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/patients`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ patient_id: patientId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create patient');
  }
}

export async function getReadings(patientId: string): Promise<BPReading[]> {
  const response = await fetch(`${API_BASE_URL}/readings/${patientId}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch readings');
  }
  return response.json();
}

export async function addReading(reading: Omit<BPReading, 'id' | 'created_at'>): Promise<BPReading> {
  const response = await fetch(`${API_BASE_URL}/readings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(reading),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to add reading');
  }

  return response.json();
}

export class BPApi {
  async login(patientId: string) {
    try {
      const exists = await checkPatientExists(patientId);
      if (!exists) {
        throw new Error('Patient not found');
      }
      return { patient_id: patientId };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async addReading(reading: Omit<BPReading, 'id'>) {
    try {
      return addReading(reading);
    } catch (error) {
      console.error('Add reading error:', error);
      throw error;
    }
  }

  async getReadings(patientId: string) {
    try {
      return getReadings(patientId);
    } catch (error) {
      console.error('Get readings error:', error);
      throw error;
    }
  }

  async createPatient(patientId: string) {
    try {
      await createPatient(patientId);
      return { patient_id: patientId };
    } catch (error) {
      console.error('Create patient error:', error);
      throw error;
    }
  }
} 